// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The language and currency a component renders in (01 §6, 08 §3).
//
// **A subscription, not a prop drilled through the tree.** The language control is on the settings
// page and the things that read it are the paywall, the roster, the account panel and every error
// toast — none of which are below it. Threading a language through every component between them
// would mean every component in the product has a language parameter, including the ones that have
// no words in them.
//
// **The same shape `ThemeController` already uses**, deliberately: a module-level value, a set of
// listeners, and a hook that subscribes. A second pattern for the same job would be a second thing
// to learn for no reason.
//
// **Currency travels with it.** They are different facts — a French-speaking American is a real
// person and so is an English-speaking Canadian — so they are stored separately and chosen
// separately. They live in one module because they change in the same place, are read by the same
// components, and a player who has just switched one is the most likely person in the world to
// switch the other.

import { useCallback, useEffect, useState } from 'react'
import {
  applyDocumentLanguage,
  localeOf,
  readLanguage,
  writeLanguage,
  type Language,
} from './language'
import { readCurrency, writeCurrency } from './currency'
import { translatorFor, type Translator } from './index'
import type { Currency } from '@src/ui/pricingCatalog'

type Listener = () => void

const listeners = new Set<Listener>()

let current: Language | undefined
let currentCurrency: Currency | undefined

function language(): Language {
  if (current === undefined) current = readLanguage()
  return current
}

function currency(): Currency {
  if (currentCurrency === undefined) currentCurrency = readCurrency()
  return currentCurrency
}

function announce(): void {
  for (const listener of [...listeners]) listener()
}

/**
 * Change the language for the whole client.
 *
 * Writes it, tells the document, and wakes every subscriber — in that order, so a listener that
 * reads the document during its render sees the new value rather than the old one.
 */
export function setLanguage(next: Language): void {
  if (next === current) return
  current = next
  writeLanguage(next)
  applyDocumentLanguage(next)
  announce()
}

export function setCurrency(next: Currency): void {
  if (next === currentCurrency) return
  currentCurrency = next
  writeCurrency(next)
  announce()
}

export interface Localisation {
  readonly language: Language
  readonly currency: Currency
  /** The BCP 47 locale, for `Intl` and for dates. */
  readonly locale: string
  readonly t: Translator
  readonly setLanguage: (next: Language) => void
  readonly setCurrency: (next: Currency) => void
}

/**
 * Everything a component needs to render words and prices.
 *
 * The translator is rebuilt whenever the language changes and is otherwise stable, so a component
 * that lists `t` in a dependency array is not re-running its effects on every render.
 */
export function useLocalisation(): Localisation {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const listener = (): void => setTick((value) => value + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const active = language()
  const money = currency()
  // `tick` is the subscription's whole contribution: it is read here so the memo below is rebuilt
  // when a change is announced, and it is otherwise meaningless.
  void tick

  const t = useCallback<Translator>((key, values) => translatorFor(active)(key, values), [active])

  return {
    language: active,
    currency: money,
    locale: localeOf(active),
    t,
    setLanguage,
    setCurrency,
  }
}

/**
 * Put the stored language on `<html lang>` at start-up.
 *
 * Called once from `main.tsx`, beside the theme. Not done at module load: a module that touched the
 * document on import would be a module no test could import without a DOM.
 */
export function initialiseLanguage(): void {
  applyDocumentLanguage(language())
}
